import { useEffect, useMemo, useState } from 'react';
import { NotFound, PageSection } from '@src/components';
import { useLazyGetRegionsQuery } from '@src/services/region/regionApiSlice';
import { SearchInput } from './SearchInput';
import { RegionList } from '../../Region/RegionList';
import { SelectedFilters } from './SelectedFilters';
import { DataTableSkeleton } from '@devotel/design-system';
import { StoreTab } from '../Store';
import { Sort } from './Sort';
import { GlobalIcon, GlobalRefreshIcon } from '@src/components/svg';
import { Button } from 'antd';
type SortOption = {
  type: string;
  direction: 'asc' | 'desc' | null;
};

interface RegionViewProps {
  onTabChange: (value: StoreTab) => void;
}

export function RegionView({ onTabChange }: RegionViewProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSort, setSelectedSort] = useState<SortOption>({
    type: '',
    direction: null,
  });

  const [getRegions, { data: regions, isLoading, isFetching }] =
    useLazyGetRegionsQuery();

  useEffect(() => {
    getRegions({ includeCountries: true });
  }, []);

  const filteredAndSortedRegions = useMemo(() => {
    if (!regions) return [];

    let result = [...regions];

    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter((region) =>
        region.name.toLowerCase().includes(lowerQuery)
      );
    }

    const { direction, type } = selectedSort;

    if (direction && type) {
      result.sort((a, b) => {
        let aValue: string | number = '';
        let bValue: string | number = '';

        switch (type) {
          case 'alphabetical':
            aValue = a.name;
            bValue = b.name;
            break;
          case 'popular':
            aValue = a.clickCount || 0;
            bValue = b.clickCount || 0;
            break;
          default:
            return 0;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          return direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        return 0;
      });
    } else {
      result.sort((a, b) => a.name.localeCompare(b.name));
    }

    return result;
  }, [regions, searchQuery, selectedSort]);

  if (isLoading || isFetching) return <DataTableSkeleton />;

  return (
    <>
      <PageSection className='custom-scrollbar bg-wl-background py-4 px-6 mb-4'>
        <div className='flex flex-col md:flex-row md:justify-between md:items-center gap-2'>
          <div className='flex items-center gap-2 w-full md:w-auto order-1 sm:order-2'>
            <div className='w-full md:w-64'>
              <SearchInput value={searchQuery} onChange={setSearchQuery} />
            </div>
            <Sort
              selectedSort={selectedSort}
              onConfirm={(selected: Record<string, unknown>) => {
                if (Object.keys(selected).length === 0) {
                  setSelectedSort({
                    type: '',
                    direction: null,
                  });
                } else {
                  const [type, direction] = Object.entries(selected)[0];
                  const validDirection =
                    direction === 'asc' || direction === 'desc'
                      ? direction
                      : null;
                  setSelectedSort({
                    type,
                    direction: validDirection,
                  });
                }
              }}
            />
          </div>

          <div className='flex gap-2 order-2 sm:order-1'>
            <Button
              size='large'
              variant='text'
              className='w-1/2 md:w-fit border-none shadow-none hover:!bg-transparent hover:!text-wl-primary text-wl-on-background bg-transparent'
              onClick={() => onTabChange('country')}
            >
              <span className='flex gap-2'>
                <GlobalRefreshIcon />
                <span className='text-M-16'>Countries</span>
              </span>
            </Button>
            <Button
              size='large'
              className='w-1/2 md:w-fit rounded-xl !bg-wl-surface !border-none'
            >
              <span className='flex gap-2 text-wl-primary'>
                <GlobalIcon /> <span className='text-B-16'>Regions</span>
              </span>
            </Button>
          </div>
        </div>
      </PageSection>

      <SelectedFilters
        selectedSort={selectedSort}
        onClearSort={() => setSelectedSort({ type: '', direction: null })}
      />
      {filteredAndSortedRegions.length > 0 ? (
        <RegionList regions={filteredAndSortedRegions} />
      ) : (
        <NotFound />
      )}
    </>
  );
}
